import { createFileRoute } from '@tanstack/react-router'
import { Stack } from '@astryxdesign/core/Stack'
import { Card } from '@astryxdesign/core/Card'
import { Text } from '@astryxdesign/core/Text'
import { SegmentedControl, SegmentedControlItem } from '@astryxdesign/core/SegmentedControl'
import { RadioList, RadioListItem } from '@astryxdesign/core/RadioList'
import {
  TEXT_SCALES, setTextScale, setThemeMode, useDisplay,
  type TextScale, type ThemeMode,
} from '#/lib/display'

export const Route = createFileRoute('/manage/accessibility')({ component: Accessibility })

/**
 * Both settings belong to the phone, not the trip. Papa reading at 130% should
 * not enlarge the type on anyone else's screen, and a preference that has to
 * reach a Worker before it applies is a preference that does nothing on a train
 * with no signal — so these are local and instant, and say so on the screen.
 */
function Accessibility() {
  const { scale, mode } = useDisplay()

  return (
    <Stack direction="vertical" gap={4}>
      <Stack direction="vertical" gap={0}>
        <Text type="display-3" display="block">Accessibility</Text>
        <Text type="supporting">
          These apply to this phone only, and stay set the next time you open
          the app.
        </Text>
      </Stack>

      <Card>
        <Stack direction="vertical" gap={3}>
          <Stack direction="vertical" gap={0}>
            <Text type="label">Text size</Text>
            <Text type="supporting">Changes every screen, including this one.</Text>
          </Stack>
          {/* A row, not a strip. Four words side by side truncate as soon as
              the type grows — "Larger" and "Largest" both became "Larg…", so
              the control stopped being readable at exactly the setting
              somebody needing it would be on. */}
          <RadioList
            label="Text size"
            isLabelHidden
            value={String(scale)}
            onChange={(v) => setTextScale(Number(v) as TextScale)}
          >
            {TEXT_SCALES.map((s) => (
              <RadioListItem key={s.value} value={String(s.value)} label={s.label} />
            ))}
          </RadioList>
          {/* Shows the effect on the spot, so you are not switching blind. */}
          <Text type="supporting">
            Sample: Chai and samosa at Gulab Ji, ₹120, paid by Papa.
          </Text>
        </Stack>
      </Card>

      <Card>
        <Stack direction="vertical" gap={3}>
          <Stack direction="vertical" gap={0}>
            <Text type="label">Appearance</Text>
            <Text type="supporting">
              System follows whatever your phone is set to, including its own
              schedule.
            </Text>
          </Stack>
          <SegmentedControl
            label="Appearance"
            layout="fill"
            value={mode}
            onChange={(v) => setThemeMode(v as ThemeMode)}
          >
            <SegmentedControlItem value="light" label="Light" />
            <SegmentedControlItem value="dark" label="Dark" />
            <SegmentedControlItem value="system" label="System" />
          </SegmentedControl>
        </Stack>
      </Card>
    </Stack>
  )
}
